import { jsPDF } from "jspdf";

export interface Item {
    id: string;
    name: string;
    quantity_fluid_type1?: number;
    quantity_fluid_type2?: number;
    quantity_container?: number;
    remove?: boolean;
}

export interface InventoryPdfOptions {
    date?: Date;
    performedBy?: string;
    checkedBy?: string;
    fileName?: string;
}

// ============================================================
// FONTY
// ============================================================

const fontCache = new Map<string, Promise<string>>();

function loadFontBase64(url: string): Promise<string> {
    if (!fontCache.has(url)) {
        fontCache.set(
            url,
            fetch(url)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(
                            `Font ${url}: ${response.status}`
                        );
                    }

                    return response.arrayBuffer();
                })
                .then(buffer => {
                    const bytes = new Uint8Array(buffer);

                    let binary = "";

                    for (
                        let i = 0;
                        i < bytes.length;
                        i += 0x8000
                    ) {
                        binary += String.fromCharCode(
                            ...bytes.subarray(i, i + 0x8000)
                        );
                    }

                    return btoa(binary);
                })
        );
    }

    return fontCache.get(url)!;
}

const FONTS = {
    normal: "/fonts/Roboto-Regular.ttf",
    bold: "/fonts/Roboto-Bold.ttf",
};

// ============================================================
// FORMÁT DATA
// ============================================================

const formatInventoryDate = (date: Date): string => {
    const pad = (value: number) =>
        String(value).padStart(2, "0");

    return (
        `${pad(date.getDate())}.` +
        `${pad(date.getMonth() + 1)}.` +
        `${date.getFullYear()} ` +
        `${pad(date.getHours())}:` +
        `${pad(date.getMinutes())} hod`
    );
};

// ============================================================
// FORMÁT ČÍSLA
// ============================================================

const formatNumber = (value: number): string => {
    return value.toLocaleString("cs-CZ");
};

// ============================================================
// EXPORT
// ============================================================

export const exportInventoryToPdf = async (
    items: readonly Item[],
    options?: InventoryPdfOptions
) => {
    const {
        date = new Date(),
        performedBy = "",
        checkedBy = "",
        fileName = "inventura.pdf",
    } = options ?? {};

    const visibleItems = items.filter(
        item => !item.remove
    );

    // ========================================================
    // SOUČTY
    // ========================================================

    const totalFluidType1 = visibleItems.reduce(
        (sum, item) =>
            sum + (Number(item.quantity_fluid_type1) || 0),
        0
    );

    const totalFluidType2 = visibleItems.reduce(
        (sum, item) =>
            sum + (Number(item.quantity_fluid_type2) || 0),
        0
    );

    const totalContainers = visibleItems.reduce(
        (sum, item) =>
            sum + (Number(item.quantity_container) || 0),
        0
    );

    // ========================================================
    // PDF
    // ========================================================

    const doc = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
    });

    // ========================================================
    // FONTY
    // ========================================================

    for (const [style, url] of Object.entries(FONTS)) {
        const file = url.split("/").pop()!;

        doc.addFileToVFS(
            file,
            await loadFontBase64(url)
        );

        doc.addFont(
            file,
            "Roboto",
            style
        );
    }

    // ========================================================
    // ZÁKLADNÍ ROZMĚRY
    // ========================================================

    const pageWidth =
        doc.internal.pageSize.getWidth();

    const pageHeight =
        doc.internal.pageSize.getHeight();

    const margin = 12;

    const tableX = margin;
    const tableWidth = pageWidth - margin * 2;

    // šířky sloupců
    const nameWidth = 90;
    const numericColumnScale =
        (tableWidth - nameWidth) / (tableWidth - 70);
    const type1Width = 40 * numericColumnScale;
    const type2Width = 40 * numericColumnScale;
    const ibcWidth =
        tableWidth -
        nameWidth -
        type1Width -
        type2Width;

    const columnX = [
        tableX,
        tableX + nameWidth,
        tableX + nameWidth + type1Width,
        tableX + nameWidth + type1Width + type2Width,
    ];

    doc.setLineWidth(0.25);
    doc.setDrawColor(0);
    doc.setTextColor(0);

    // ========================================================
    // HELPERS
    // ========================================================

    const text = (
        value: string | number | null | undefined,
        x: number,
        y: number,
        options?: {
            size?: number;
            bold?: boolean;
            align?: "left" | "center" | "right";
        }
    ) => {
        const {
            size = 9,
            bold = false,
            align = "left",
        } = options ?? {};

        doc.setFontSize(size);

        doc.setFont(
            "Roboto",
            bold ? "bold" : "normal"
        );

        doc.text(
            value == null ? "" : String(value),
            x,
            y,
            {
                align,
            }
        );
    };

    // ========================================================
    // HLAVIČKA STRÁNKY
    // ========================================================

    const drawPageHeader = () => {
        text(
            "Inventura / fyzická kontrola stavu skladových zásob",
            pageWidth / 2,
            18,
            {
                size: 14,
                bold: true,
                align: "center",
            }
        );

        text(
            `odmrazovacích kapalin ke dni ${formatInventoryDate(date)}`,
            pageWidth / 2,
            26,
            {
                size: 12,
                bold: true,
                align: "center",
            }
        );
    };

    // ========================================================
    // HLAVIČKA TABULKY
    // ========================================================

    const headerHeight = 14;

    const drawTableHeader = (y: number) => {
        doc.rect(
            tableX,
            y,
            tableWidth,
            headerHeight
        );

        // svislé čáry
        doc.line(
            columnX[1],
            y,
            columnX[1],
            y + headerHeight
        );

        doc.line(
            columnX[2],
            y,
            columnX[2],
            y + headerHeight
        );

        doc.line(
            columnX[3],
            y,
            columnX[3],
            y + headerHeight
        );

        text(
            "DEICING FLUID TYPE I",
            columnX[1] + type1Width / 2,
            y + 8,
            {
                size: 8,
                bold: true,
                align: "center",
            }
        );

        text(
            "DEICING FLUID TYPE II",
            columnX[2] + type2Width / 2,
            y + 8,
            {
                size: 8,
                bold: true,
                align: "center",
            }
        );

        text(
            "IBC",
            columnX[3] + ibcWidth / 2,
            y + 8,
            {
                size: 8,
                bold: true,
                align: "center",
            }
        );

        return y + headerHeight;
    };

    // ========================================================
    // ŘÁDEK TABULKY
    // ========================================================

    const rowHeight = 10;
    const nameLineHeight = 4;

    const getNameLayout = (name: string, bold = false) => {
        doc.setFontSize(9);
        doc.setFont("Roboto", bold ? "bold" : "normal");

        const lines: string[] = doc.splitTextToSize(name, nameWidth - 4);

        return {
            lines,
            height: Math.max(rowHeight, lines.length * nameLineHeight + 6),
        };
    };

    const drawRow = (
        y: number,
        values: [
            string,
            string | number,
            string | number,
            string | number
        ],
        bold = false
    ) => {
        const { lines, height } = getNameLayout(values[0], bold);
        const textY = y + height / 2 + 1.5;

        doc.rect(
            tableX,
            y,
            tableWidth,
            height
        );

        doc.line(
            columnX[1],
            y,
            columnX[1],
            y + height
        );

        doc.line(
            columnX[2],
            y,
            columnX[2],
            y + height
        );

        doc.line(
            columnX[3],
            y,
            columnX[3],
            y + height
        );

        // název
        lines.forEach((line, index) => text(
            line,
            tableX + 2,
            textY - (lines.length - 1) * nameLineHeight / 2 + index * nameLineHeight,
            {
                size: 9,
                bold,
            }
        ));

        // Type I
        text(
            values[1],
            columnX[1] + type1Width / 2,
            textY,
            {
                size: 9,
                bold,
                align: "center",
            }
        );

        // Type II
        text(
            values[2],
            columnX[2] + type2Width / 2,
            textY,
            {
                size: 9,
                bold,
                align: "center",
            }
        );

        // IBC
        text(
            values[3],
            columnX[3] + ibcWidth / 2,
            textY,
            {
                size: 9,
                bold,
                align: "center",
            }
        );

        return y + height;
    };

    // ========================================================
    // PRVNÍ STRÁNKA
    // ========================================================

    drawPageHeader();

    let y = 35;

    y = drawTableHeader(y);

    // ========================================================
    // POLOŽKY
    // ========================================================

    for (const item of visibleItems) {
        const itemHeight = getNameLayout(item.name ?? "").height;

        // pokud se řádek nevejde na stránku
        if (y + itemHeight > pageHeight - 15) {
            doc.addPage();

            y = 15;

            // na další stránce znovu hlavička tabulky
            y = drawTableHeader(y);
        }

        y = drawRow(
            y,
            [
                item.name ?? "",

                formatNumber(
                    Number(
                        item.quantity_fluid_type1
                    ) || 0
                ),

                formatNumber(
                    Number(
                        item.quantity_fluid_type2
                    ) || 0
                ),

                formatNumber(
                    Number(
                        item.quantity_container
                    ) || 0
                ),
            ]
        );
    }

    // ========================================================
    // CELKEM
    // ========================================================

    if (y + rowHeight > pageHeight - 15) {
        doc.addPage();

        y = 15;

        y = drawTableHeader(y);
    }

    y = drawRow(
        y,
        [
            "Celkem",
            formatNumber(totalFluidType1),
            formatNumber(totalFluidType2),
            formatNumber(totalContainers),
        ],
        true
    );

    // ========================================================
    // PODPISY
    // ========================================================

    y += 12;

    // pokud se podpisy nevejdou
    if (y + 40 > pageHeight - 10) {
        doc.addPage();

        y = 20;
    }

    // ========================================================
    // KONTROLU PROVEDL
    // ========================================================

    text(
        "Kontrolu provedl:",
        margin,
        y,
        {
            size: 10,
        }
    );

    text(
        performedBy,
        margin + 42,
        y,
        {
            size: 10,
        }
    );

    // podpisová čára
    // doc.line(
    //     margin + 42,
    //     y + 2,
    //     margin + 110,
    //     y + 2
    // );

    // ========================================================
    // KONTROLOVAL / OVĚŘIL
    // ========================================================

    y += 18;

    text(
        "Kontroloval / ověřil:",
        margin,
        y,
        {
            size: 10,
        }
    );

    text(
        checkedBy,
        margin + 42,
        y,
        {
            size: 10,
        }
    );

    // podpisová čára
    // doc.line(
    //     margin + 42,
    //     y + 2,
    //     margin + 110,
    //     y + 2
    // );

    // ========================================================
    // ULOŽENÍ
    // ========================================================

    const finalFileName =
        fileName.toLowerCase().endsWith(".pdf")
            ? fileName
            : `${fileName}.pdf`;

    doc.save(finalFileName);
};
